import { type ApiProblem, type ApiSchemas, call } from '@rental/contracts/client'
import {
  type CheckoutBlock,
  checkoutBlocks,
  coveringReport,
  type EligibilityStatus,
  type StaffLocale,
} from '@rental/domain'
import { format, formatDateTime, formatTime, localized, type StaffMessages } from '@rental/i18n'
import { Button, Checkbox, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, CircleAlert, TabletSmartphone } from 'lucide-react'
import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { type CanDo, canDo } from '../booking-can'
import { bookingQuery } from '../booking-query'
import { UnsentInspectionNotice } from '../capture/CaptureUi'
import { useUnsentInspection } from '../capture/hooks'
import { ActionRail, RAIL_FORM, RAIL_STEPS } from '../components/ActionRail'
import { StatusBadge } from '../components/BookingItem'
import { MoneyStrip } from '../components/MoneyStrip'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { Section } from '../components/Section'
import { type ChipTone, StatusChip } from '../components/StatusChip'
import { SubPanel } from '../components/SubPanel'
import { LoadState } from '../components/states'
import { FIELD, FIELD_ROW, ProblemNotice, StaffInput, StaffSelect } from '../components/ui'
import { DepositPanel } from '../deposit/DepositPanel'
import { DocumentCapture, DocumentList } from '../documents/Documents'
import { reasonText } from '../eligibility-words'
import { noImplicitSubmit } from '../forms'
import { CARGOS_POLL_MS, CargosPanel, cargosInFlight } from '../handover/CargosPanel'
import { IdentitySection } from '../handover/IdentitySection'
import {
  type FormReason,
  type Readiness,
  readiness,
  readingKm,
  type ServerBlocks,
  serverBlocks,
} from '../handover/readiness'
import { secondDriverFacts, secondIdentityId } from '../handover/second-driver'
import { useLocale } from '../i18n'
import { InspectionPanel } from '../inspection/InspectionPanel'
import { problemOf } from '../problems'
import { refreshAfterChange } from '../refresh'
import { FUEL_LEVELS, fuelLabel, signedOnIpad, termsStop } from '../rental'
import { ContractPdfLink } from '../signing/ContractPdfLink'

const route = getRouteApi('/bookings/$code/checkout')

type Sheet = ApiSchemas['CheckoutSheet']

const ELIGIBILITY_TONE: Readonly<Record<EligibilityStatus, ChipTone>> = {
  eligible: 'success',
  ineligible: 'danger',
  unknown: 'warning',
}

/**
 * OPS-1 and OPS-4: hand the vehicle over, with the checks the counter makes.
 * UI-1d: the steps on panes, and beside them, from 1024 px, a rail that stays
 * in sight with the one Consegna button and what it waits for.
 */
export function CheckoutScreen() {
  const { locale, messages: m } = useLocale()
  const { code } = route.useParams()
  const upper = code.toUpperCase()
  const sheet = useQuery({
    queryKey: ['checkout', upper],
    queryFn: () =>
      load(call(api.GET('/v1/staff/bookings/{code}/checkout', { params: { path: { code } } }))),
    // A report on its way to CaRGOS: read again until the worker answers.
    refetchInterval: (query) => (cargosInFlight(query.state.data) ? CARGOS_POLL_MS : false),
  })
  // S9: what this person may do on it, as the booking's own answer says.
  const booking = useQuery(bookingQuery(code))
  const problem = problemOf(sheet.error) ?? problemOf(booking.error)
  const data = sheet.data
  return (
    <>
      <PageHeader
        back={
          <Link
            to="/bookings/$code"
            params={{ code: upper }}
            className="flex min-h-touch items-center gap-1 self-start"
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
            {m.checkout.back}
          </Link>
        }
        title={format(m.checkout.title, { code: upper })}
        status={data ? <StatusBadge status={data.status} /> : undefined}
        meta={
          data
            ? [
                localized(data.category.name, locale),
                `${formatDateTime(locale, data.startsAt)} → ${formatDateTime(locale, data.endsAt)}`,
              ].join(' · ')
            : undefined
        }
      />
      <LoadState
        data={data && booking.data ? { sheet: data, can: canDo(booking.data.can) } : undefined}
        problem={problem}
      >
        {(loaded) => <CheckoutForm sheet={loaded.sheet} can={loaded.can} />}
      </LoadState>
    </>
  )
}

function CheckoutForm({ sheet, can }: { sheet: Sheet; can: CanDo }) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Back from the customer's signature (the screen was locked meanwhile): the car they signed for.
  const [vehicleId, setVehicleId] = useState(sheet.vehicleId ?? sheet.contract.vehicleId ?? '')
  const [km, setKm] = useState('')
  const [fuel, setFuel] = useState('')
  const [documents, setDocuments] = useState(false)
  const [paper, setPaper] = useState(false)
  const [contractReference, setContractReference] = useState('')
  const [override, setOverride] = useState(false)
  const [overrideReason, setOverrideReason] = useState('')
  const [leaveUnsent, setLeaveUnsent] = useState(false)
  const [unsentReason, setUnsentReason] = useState('')
  const [busy, setBusy] = useState(false)
  // Handed over: the screen stays as it is until the booking's page replaces it.
  const [leaving, setLeaving] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  // The blocks the server named when it last refused, for the sheet it judged. A sheet
  // read again with something changed is judged afresh, here and on the server.
  const [refusal, setRefusal] = useState<{ sheet: Sheet; blocks: ServerBlocks } | null>(null)
  // S4: the car leaves once its inspection has reached the server from this iPad.
  const inspection = useUnsentInspection(
    vehicleId ? { bookingCode: sheet.code, stage: 'checkout', vehicleId } : null,
  )
  const leavingUnsent = inspection.unsent.length > 0 && leaveUnsent && sheet.canOverride

  const chosen = sheet.vehicles.find((vehicle) => vehicle.id === vehicleId)
  // CTR-2: signed on the iPad, for this booking as it stands and the car leaving.
  const digital = signedOnIpad(sheet, vehicleId)
  // The server decides; packages/domain's rules, the server's own, show the
  // counter what stops the hand-over before anyone presses.
  const identity = sheet.identity.latest
  const { cover } = coveringReport(sheet.cargos.transmissions, {
    vehicleId,
    reservationVersion: sheet.version,
    identityId: identity?.id ?? null,
    secondIdentityId: secondIdentityId(sheet),
  })
  const blocks = checkoutBlocks({
    eligibilityStatus: sheet.eligibility.status,
    identityRecorded: identity !== null,
    birthDateDiffers:
      identity !== null &&
      sheet.identity.bookingBirthDate !== null &&
      sheet.identity.bookingBirthDate !== identity.birthDate,
    cargosRequired: sheet.cargos.state === 'required',
    cargosCover: cover,
    contract: digital ? 'digital' : paper ? 'paper' : 'none',
    contractRequired: sheet.contractRequired,
    depositRequired: sheet.deposit.required,
    depositHeld: sheet.deposit.current?.state === 'held',
    secondDriver: secondDriverFacts(sheet),
  })
  const stopped = termsStop(sheet, vehicleId)
  const state = readiness(
    {
      now: Date.now(),
      opensAt: sheet.opensAt,
      endsAt: sheet.endsAt,
      stopped: stopped !== null,
      vehicleId,
      km,
      fuel,
      documents,
      inspection: {
        known: inspection.known,
        unsent: inspection.unsent.length,
        leaving: leavingUnsent,
        leaveReason: unsentReason,
      },
      override: { chosen: override, reason: overrideReason },
    },
    blocks,
    refusal?.sheet === sheet ? refusal.blocks : null,
  )

  async function submit(event: FormEvent) {
    event.preventDefault()
    const kmOut = readingKm(km)
    if (!state.ready || kmOut === null) return
    setProblem(null)
    // The server's answer stands until it is asked again.
    setRefusal(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/bookings/{code}/checkout', {
        params: { path: { code: sheet.code } },
        body: {
          vehicleId,
          kmOut,
          fuelOut: Number(fuel),
          documentsChecked: true,
          ...(paper && !digital ? { contractSigned: true as const } : {}),
          ...(contractReference.trim() ? { contractReference: contractReference.trim() } : {}),
          ...(state.overriding ? { override: { reason: overrideReason.trim() } } : {}),
          ...(leavingUnsent
            ? {
                unsentInspection: {
                  count: inspection.unsent.length,
                  reason: unsentReason.trim(),
                },
              }
            : {}),
        },
      }),
    )
    if (result.ok) {
      // Busy until the booking's page is up: a second press must not hand over again.
      setLeaving(true)
      await refreshAfterChange(queryClient, sheet.code)
      void navigate({
        to: '/bookings/$code',
        params: { code: sheet.code },
        search: { done: 'checkout' },
      })
      return
    }
    if (takeOver(result.problem)) {
      setBusy(false)
      return
    }
    setProblem(result.problem)
    // Blocked by what the screen did not see: the server's list joins the rail's.
    const named = serverBlocks(result.problem)
    if (named) setRefusal({ sheet, blocks: named })
    // Refused: the sheet is read again for the facts behind it (blocks, a car
    // taken meanwhile, a hand-over already made from another iPad). The button
    // waits for that answer: a second press would be refused again.
    if (result.problem.kind === 'api') {
      await queryClient.invalidateQueries({ queryKey: ['checkout', sheet.code] })
    }
    setBusy(false)
  }

  // Only a confirmed booking leaves (the server refuses any other): said, not offered.
  if (sheet.status !== 'confirmed' && !leaving) {
    const words = <p>{m.checkout.notConfirmed}</p>
    // A refused press that finds the car gone has lost its button with the form: this takes
    // the focus. Otherwise a polite status, heard if it comes while the screen is open.
    return problem ? (
      <OutcomeNotice tone="warning">{words}</OutcomeNotice>
    ) : (
      <Notice tone="warning" live="polite">
        {words}
      </Notice>
    )
  }

  return (
    <form onSubmit={submit} onKeyDown={noImplicitSubmit} className={RAIL_FORM} noValidate>
      <div className={RAIL_STEPS}>
        {stopped ? (
          // C1: said before anyone starts; the hand-over and the iPad refuse it anyway.
          <Notice tone="danger">
            <p>{stopped === 'draft' ? m.checkout.termsDraft : m.checkout.termsMissing}</p>
          </Notice>
        ) : sheet.companyIncomplete && !digital ? (
          <Notice tone="warning">
            <p>{m.checkout.companyIncomplete}</p>
          </Notice>
        ) : null}

        <IdentitySection sheet={sheet} />
        {/* Q-162: the second driver's, recorded the same way; nothing without one. */}
        <IdentitySection sheet={sheet} driver="second" />

        <Section title={m.checkout.vehicle}>
          <div className={FIELD_ROW}>
            <Field className={FIELD} label={m.checkout.vehicle} required>
              {(control) => (
                <StaffSelect
                  {...control}
                  value={vehicleId}
                  onChange={(event) => setVehicleId(event.target.value)}
                >
                  <option value="">{m.checkout.chooseVehicle}</option>
                  {sheet.vehicles.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id} disabled={!vehicle.free}>
                      {vehicle.plate} · {vehicle.make} {vehicle.model}
                      {vehicle.free ? '' : ` (${m.checkout.taken})`}
                    </option>
                  ))}
                </StaffSelect>
              )}
            </Field>
          </div>
        </Section>

        <ContractSection
          sheet={sheet}
          vehicleId={vehicleId}
          paper={paper}
          onPaper={setPaper}
          reference={contractReference}
          onReference={setContractReference}
        />

        <CargosPanel
          sheet={sheet}
          vehicleId={vehicleId}
          contract={digital ? 'digital' : paper ? 'paper' : 'none'}
          can={can}
        />

        <Section title={m.checkout.readings}>
          <div className={FIELD_ROW}>
            <Field
              className={FIELD}
              label={m.checkout.km}
              required
              hint={
                chosen?.odometerKm != null
                  ? format(m.checkout.kmHint, { km: chosen.odometerKm })
                  : undefined
              }
            >
              {(control) => (
                <StaffInput
                  {...control}
                  inputMode="numeric"
                  autoComplete="off"
                  // Whole kilometres, seven digits at most, as the server takes them.
                  maxLength={7}
                  value={km}
                  onChange={(event) => setKm(event.target.value.replace(/\D/g, ''))}
                />
              )}
            </Field>
            <Field className={FIELD} label={m.checkout.fuel} required>
              {(control) => (
                <StaffSelect
                  {...control}
                  value={fuel}
                  onChange={(event) => setFuel(event.target.value)}
                >
                  <option value="" />
                  {FUEL_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {fuelLabel(level, m)}
                    </option>
                  ))}
                </StaffSelect>
              )}
            </Field>
          </div>
        </Section>

        <Section title={m.inspection.title}>
          {vehicleId ? (
            <InspectionPanel code={sheet.code} vehicleId={vehicleId} can={can} />
          ) : (
            <p>{m.inspection.chooseVehicle}</p>
          )}
          <UnsentInspectionNotice
            stage="checkout"
            unsent={inspection.unsent}
            elsewhere={inspection.elsewhere}
            known={inspection.known}
            canOverride={sheet.canOverride}
            leave={leaveUnsent}
            onLeave={setLeaveUnsent}
            reason={unsentReason}
            onReason={setUnsentReason}
          />
        </Section>

        <Section title={m.checkout.checks}>
          <div className="flex flex-wrap items-center gap-3">
            <StatusChip tone={ELIGIBILITY_TONE[sheet.eligibility.status]}>
              {m.eligibility[sheet.eligibility.status]}
            </StatusChip>
            <span className="text-text-muted">{localized(sheet.category.name, locale)}</span>
          </div>
          {sheet.eligibility.reasons.length > 0 ? (
            <>
              <p className="font-medium">{m.booking.toCheck}</p>
              <ul className="flex list-disc flex-col gap-1 ps-6">
                {sheet.eligibility.reasons.map((reason) => (
                  <li key={reason.code}>{reasonText(reason, locale, m)}</li>
                ))}
              </ul>
            </>
          ) : null}
          <div className="flex flex-col gap-2">
            <h3 className="text-lg">{m.booking.documents}</h3>
            <DocumentList documents={sheet.documents} can={can} />
          </div>
          <DocumentCapture code={sheet.code} can={can} />
          <Checkbox
            label={m.checkout.documents}
            checked={documents}
            onChange={(event) => setDocuments(event.target.checked)}
          />
        </Section>

        <Section title={m.checkout.deposit}>
          <DepositPanel
            code={sheet.code}
            deposit={sheet.deposit}
            status={sheet.status}
            balanceCents={sheet.money.balanceCents}
            endsAt={sheet.endsAt}
            can={can}
          />
        </Section>
      </div>

      <ReadinessRail
        sheet={sheet}
        state={state}
        busy={busy || leaving}
        problem={problem}
        override={override}
        onOverride={setOverride}
        overrideReason={overrideReason}
        onOverrideReason={setOverrideReason}
      />
    </form>
  )
}

/** A reason the form gives, in the counter's words. */
function reasonWords(
  reason: FormReason,
  sheet: Sheet,
  locale: StaffLocale,
  m: StaffMessages,
): string {
  if (reason === 'early')
    return format(m.checkout.opensAt, { time: formatTime(locale, sheet.opensAt) })
  return m.checkout.why[reason]
}

/**
 * The hand-over's rail (`ActionRail`): the one Consegna button and what the
 * form lacks; under them, the two notices of blocks, each block said there
 * and nowhere else, and what is owed, which is information and never a
 * reason to wait.
 */
function ReadinessRail({
  sheet,
  state,
  busy,
  problem,
  override,
  onOverride,
  overrideReason,
  onOverrideReason,
}: {
  sheet: Sheet
  state: Readiness
  busy: boolean
  problem: ApiProblem | null
  override: boolean
  onOverride: (override: boolean) => void
  overrideReason: string
  onOverrideReason: (reason: string) => void
}) {
  const { locale, messages: m } = useLocale()
  const hardId = useId()
  const overridableId = useId()
  return (
    <ActionRail
      title={m.checkout.railTitle}
      action={m.checkout.submit}
      detailsLabel={m.checkout.railDetails}
      ready={state.ready}
      busy={busy}
      reasons={state.reasons.map((reason) => reasonWords(reason, sheet, locale, m))}
      // The blocks still standing name the button too; an override given lifts the Owner's.
      stoppedBy={[
        ...(state.hard.length > 0 ? [hardId] : []),
        ...(state.overridable.length > 0 && !state.overriding ? [overridableId] : []),
      ]}
      problem={problem}
    >
      {state.hard.length > 0 ? (
        <BlockNotice
          title={m.checkout.mustDo}
          listId={hardId}
          blocks={state.hard}
          hint={<p>{m.checkout.mustDoHint}</p>}
        />
      ) : null}
      {state.overridable.length > 0 ? (
        <BlockNotice
          title={m.checkout.blocked}
          listId={overridableId}
          blocks={state.overridable}
          hint={sheet.canOverride ? null : <p>{m.checkout.ownerOnly}</p>}
        >
          {sheet.canOverride ? (
            <div className="flex flex-col gap-3 pt-2">
              <Checkbox
                label={m.checkout.override}
                checked={override}
                onChange={(event) => onOverride(event.target.checked)}
              />
              {override ? (
                <Field
                  label={m.checkout.overrideReason}
                  hint={m.checkout.overrideReasonHint}
                  required
                >
                  {(control) => (
                    <StaffInput
                      {...control}
                      maxLength={500}
                      value={overrideReason}
                      onChange={(event) => onOverrideReason(event.target.value)}
                    />
                  )}
                </Field>
              ) : null}
            </div>
          ) : null}
        </BlockNotice>
      ) : null}
      {/* The rental's own account, apart from the deposit: a hold is not a payment. */}
      <div className="flex flex-col gap-2">
        <MoneyStrip money={sheet.money} />
        <p className="text-sm text-text-muted">{m.checkout.moneyHint}</p>
      </div>
    </ActionRail>
  )
}

/**
 * What stops the hand-over, in the rail: the title beside its icon and the
 * blocks under it at the rail's full width, on the danger tint (DESIGN.md:
 * an icon, the word and the colour together). The Notice's icon column
 * would leave the words a third of a 288 px rail. Its words are a polite
 * status, as the notices were: a block that joins the list as the form
 * changes is said. One that leaves is not (a live region reads what comes,
 * not what goes): the button, described by what still stops it, says that.
 * The Owner's override sits outside the status, so what is typed there is
 * never read back as it changes.
 */
function BlockNotice({
  title,
  listId,
  blocks,
  hint,
  children,
}: {
  title: string
  listId: string
  blocks: readonly CheckoutBlock[]
  /** Words under the list, said with it. */
  hint: ReactNode
  /** Controls under the words, outside the status. */
  children?: ReactNode
}) {
  const { messages: m } = useLocale()
  return (
    <div className="flex flex-col gap-2 rounded-sm bg-mz-danger-tint p-3">
      <div role="status" className="flex flex-col gap-2">
        <p className="flex items-start gap-2 font-bold text-mz-danger-800">
          <CircleAlert aria-hidden="true" className="mt-1 size-4 shrink-0" />
          {title}
        </p>
        <ul id={listId} className="flex list-disc flex-col gap-1 ps-6">
          {blocks.map((block) => (
            <li key={block}>{m.checkout.blocks[block]}</li>
          ))}
        </ul>
        {hint}
      </div>
      {children}
    </div>
  )
}

/**
 * CTR-2 at the hand-over: the contract signed on the iPad for the car that
 * leaves, or the button that turns the iPad to the customer. The printed
 * contract stays as the fallback; where the iPad's is required, paper alone
 * is a block the Owner overrides (OPS-4).
 */
function ContractSection({
  sheet,
  vehicleId,
  paper,
  onPaper,
  reference,
  onReference,
}: {
  sheet: Sheet
  vehicleId: string
  paper: boolean
  onPaper: (paper: boolean) => void
  reference: string
  onReference: (reference: string) => void
}) {
  const { locale, messages: m } = useLocale()
  const { turnToCustomer } = useAccess()
  const [turning, setTurning] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const chooseFirstId = useId()
  const { contract } = sheet
  const digital = signedOnIpad(sheet, vehicleId)
  const otherVehicle = contract.status === 'signed' && vehicleId !== '' && !digital

  async function turn() {
    if (!vehicleId) return
    setProblem(null)
    setTurning(true)
    // On success the screen turns to the customer and this one goes away.
    const failed = await turnToCustomer(sheet.code, vehicleId)
    setTurning(false)
    if (failed) setProblem(failed)
  }

  return (
    <Section title={m.checkout.contractTitle}>
      {digital && contract.signedAt ? (
        <div className="flex flex-col gap-2">
          <p className="flex flex-wrap items-center gap-3">
            <StatusChip tone="success">{m.booking.contract.badge}</StatusChip>
            <span>
              {format(m.checkout.contractSigned, {
                date: formatDateTime(locale, contract.signedAt),
              })}
            </span>
          </p>
          {contract.pdf ? <ContractPdfLink code={sheet.code} /> : null}
        </div>
      ) : (
        <>
          {vehicleId === '' ? (
            <p id={chooseFirstId}>{m.checkout.contractChooseVehicle}</p>
          ) : otherVehicle ? (
            <Notice tone="warning">
              <p>{m.checkout.contractOtherVehicle}</p>
            </Notice>
          ) : contract.status === 'stale' ? (
            <Notice tone="warning">
              <p>{m.checkout.contractStale}</p>
            </Notice>
          ) : contract.status === 'prepared' ? (
            <p>{m.checkout.contractWaiting}</p>
          ) : null}
          <div className="flex flex-col gap-2">
            <div>
              <Button
                variant="staff"
                busy={turning}
                disabled={vehicleId === '' && !turning}
                // Waiting for the vehicle, the button says so: the line above is its reason.
                aria-describedby={vehicleId === '' && !turning ? chooseFirstId : undefined}
                onClick={() => void turn()}
              >
                <TabletSmartphone aria-hidden="true" className="size-6" />
                {m.checkout.turn}
              </Button>
            </div>
            <p className="text-sm text-text-muted">{m.checkout.turnHint}</p>
          </div>
          <ProblemNotice problem={problem} />
          <SubPanel title={m.checkout.paper}>
            <Checkbox
              label={m.checkout.contract}
              checked={paper}
              onChange={(event) => onPaper(event.target.checked)}
            />
            <p className="text-sm text-text-muted">
              {format(m.checkout.contractHint, { version: sheet.termsVersion ?? '—' })}
            </p>
            {sheet.contractRequired && paper ? (
              <p className="text-sm">{m.checkout.paperNeedsOwner}</p>
            ) : null}
            <div className={FIELD_ROW}>
              <Field
                className={FIELD}
                label={m.checkout.contractReference}
                optionalLabel={m.newBooking.optional}
              >
                {(control) => (
                  <StaffInput
                    {...control}
                    maxLength={100}
                    autoComplete="off"
                    value={reference}
                    onChange={(event) => onReference(event.target.value)}
                  />
                )}
              </Field>
            </div>
          </SubPanel>
        </>
      )}
    </Section>
  )
}
